import { useEffect, useState } from "react";
import { Plus, Trash2, Shield, X, Pencil, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

import { API } from "@/apiConfig";

export default function GestionUtilisateurs() {
  const { user: me, refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", prenom: "", password: "", role: "EDITOR" });
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // {id, prenom, role}

  const load = () => axios.get(`${API}/admin/users`).then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => toast.error("Accès refusé"));

  useEffect(() => { load(); }, []);

  const handleAdd = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users`, form);
      toast.success("Utilisateur créé !");
      setForm({ email: "", prenom: "", password: "", role: "EDITOR" });
      setAdding(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };

  const saveUser = async (id) => {
    try {
      await axios.patch(`${API}/admin/users/${id}`, { prenom: editingUser.prenom, email: editingUser.email, role: editingUser.role });
      toast.success("Utilisateur mis à jour");
      setEditingUser(null);
      if(id===me?.id)await refreshUser();
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Modification impossible"); }
  };

  if (me?.role !== "ADMIN" && me?.role !== "VISITOR") {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield size={32} className="text-sage/40 mx-auto mb-3" />
          <p className="font-manrope text-charcoal-muted">Accès réservé aux administrateurs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-playfair text-3xl text-charcoal">Utilisateurs</h1>
          <p className="font-manrope text-sm text-charcoal-muted">{users.length} utilisateur{users.length > 1 ? "s" : ""}</p>
        </div>
        <button onClick={() => setAdding(true)} data-testid="add-user-btn" className="btn-primary flex items-center gap-2"><Plus size={16} /> Ajouter</button>
      </div>

      {adding && (
        <div className="bg-white rounded-2xl border border-sage/10 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-manrope font-medium text-charcoal">Nouvel utilisateur</h2>
            <button onClick={() => setAdding(false)} className="p-1.5 text-charcoal-muted hover:text-charcoal"><X size={18} /></button>
          </div>
          <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Prénom *</label>
              <input type="text" value={form.prenom} onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))} required placeholder="Céline"
                className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none" />
            </div>
            <div>
              <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Email *</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required placeholder="email@exemple.fr"
                className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none" />
            </div>
            <div>
              <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Mot de passe *</label>
              <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required placeholder="Minimum 8 caractères"
                className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none" />
            </div>
            <div>
              <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Rôle</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none bg-white">
                <option value="EDITOR">Éditeur</option>
                <option value="ADMIN">Administrateur</option>
                <option value="VISITOR">Visiteur · lecture seule</option>
              </select>
            </div>
            <div className="md:col-span-2 flex gap-3">
              <button type="submit" disabled={loading} data-testid="user-save-btn" className="btn-primary flex items-center gap-2 disabled:opacity-60">
                {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Créer l'utilisateur"}
              </button>
              <button type="button" onClick={() => setAdding(false)} className="btn-secondary">Annuler</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-sage/10 overflow-hidden">
        {users.length === 0 ? (
          <div className="py-16 text-center font-manrope text-charcoal-muted">Aucun utilisateur.</div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-sage/10 bg-bone/30">
              <tr>
                {["Prénom", "Email", "Rôle", "Créé le", "Actions"].map(h => (
                  <th key={h} className="text-left font-manrope text-xs text-charcoal-muted px-5 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id || i} data-testid={`user-row-${i}`} className="border-b border-sage/5 last:border-0 hover:bg-bone/20 transition-colors">
                  <td className="px-5 py-3">
                    {editingUser?.id === u.id ? (
                      <div className="flex items-center gap-2">
                        <input autoFocus value={editingUser.prenom} onChange={e => setEditingUser(current => ({ ...current, prenom: e.target.value }))}
                          onKeyDown={e => { if (e.key === "Enter") saveUser(u.id); if (e.key === "Escape") setEditingUser(null); }}
                          className="border border-sage/30 rounded-lg px-2 py-1 font-manrope text-sm outline-none w-28" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="font-manrope text-sm font-medium text-charcoal">{u.prenom || "—"}</p>
                      </div>
                    )}
                    {u.id === me?.id && <span className="font-manrope text-xs text-sage">(vous)</span>}
                  </td>
                  <td className="px-5 py-3">
                    {editingUser?.id===u.id?<input type="email" value={editingUser.email} onChange={e=>setEditingUser(current=>({...current,email:e.target.value}))} className="w-full rounded-lg border border-sage/30 px-2 py-1 font-manrope text-sm outline-none"/>:<p className="font-manrope text-sm text-charcoal-muted">{u.email}</p>}
                  </td>
                  <td className="px-5 py-3">
                    {editingUser?.id===u.id?<select value={editingUser.role} disabled={u.id===me?.id} onChange={e=>setEditingUser(current=>({...current,role:e.target.value}))} title={u.id===me?.id?"Votre propre rôle ne peut pas être modifié ici":"Modifier les droits"} className="rounded-lg border border-sage/30 bg-white px-2 py-1 font-manrope text-xs disabled:bg-bone disabled:text-charcoal-muted"><option value="ADMIN">Administrateur</option><option value="EDITOR">Éditeur</option><option value="VISITOR">Visiteur · lecture seule</option></select>:<span className={`font-manrope text-xs px-2.5 py-1 rounded-full ${u.role === "ADMIN" ? "bg-sage/10 text-sage-dark" : u.role === "VISITOR" ? "bg-amber-100 text-amber-800" : "bg-bone text-charcoal-muted"}`}>{u.role === "ADMIN" ? "Administrateur" : u.role === "VISITOR" ? "Visiteur" : "Éditeur"}</span>}
                  </td>
                  <td className="px-5 py-3 font-manrope text-xs text-charcoal-muted">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">{editingUser?.id===u.id?<><button onClick={()=>saveUser(u.id)} aria-label="Enregistrer l'utilisateur" className="p-1.5 text-sage hover:text-sage-dark"><Check size={15}/></button><button onClick={()=>setEditingUser(null)} aria-label="Annuler la modification" className="p-1.5 text-charcoal-muted hover:text-charcoal"><X size={15}/></button></>:<button onClick={()=>setEditingUser({id:u.id,prenom:u.prenom||"",email:u.email||"",role:u.role})} aria-label={`Modifier ${u.prenom||u.email}`} className="p-1.5 text-charcoal-muted hover:text-sage"><Pencil size={15}/></button>}{u.id!==me?.id&&editingUser?.id!==u.id&&<button onClick={() => handleDelete(u.id)} data-testid={`delete-user-${i}`} className="p-1.5 text-charcoal-muted hover:text-red-500 transition-colors"><Trash2 size={15} /></button>}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
